import axios, { AxiosResponse, AxiosError } from 'axios';
import { message } from 'antd';
import { getLoginRedirectUrl } from '../utils/authRedirect';
import { isAdminOrSuperAdminRole } from '../utils/rbac';

// Base API configuration
// REACT_APP_API_URL can be either the backend root (https://bhutan-nac-repo.onrender.com)
// or the full API path (https://bhutan-nac-repo.onrender.com/api/v1) — we normalize to always use /api/v1.
function normalizeApiBaseUrl(url: string): string {
  const u = (url || '').replace(/\/+$/, '');
  if (!u) return 'http://localhost:8000/api/v1';
  if (u.endsWith('/api/v1')) return u;
  return u + '/api/v1';
}
export const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1');

/** Returns backend root URL for static uploads (e.g. company logos) */
export function getUploadsBaseUrl(): string {
  const base = API_BASE_URL.replace(/\/api\/v1\/?$/, '') || 'http://localhost:8000';
  return base;
}
const API_DEBUG = (process.env.REACT_APP_API_DEBUG || '').toLowerCase() === 'true';

/** Cashiers and other shop users stay on the till. Admin and super admin still log out. */
function shopUserKeepsSession(): boolean {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return false;
    const roleName = JSON.parse(raw)?.user?.roleName as string | undefined;
    if (!roleName) return false;
    return !isAdminOrSuperAdminRole(roleName);
  } catch {
    return false;
  }
}

// Local: allow 60s (DB connect, first query, cold start). Production: 2 min for Render cold start.
const isLocalBackend = /localhost|127\.0\.0\.1/.test(API_BASE_URL);
const REQUEST_TIMEOUT_MS = isLocalBackend ? 60000 : 120000;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** True if string looks like a JWT (header.payload.sig). */
export function isLikelyJwt(token: string): boolean {
  const t = (token || '').trim();
  if (!t || t === 'undefined' || t === 'null' || t === 'debug-token') return false;
  const parts = t.split('.');
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

export function sanitizeAuthToken(raw: string | null): string | null {
  if (raw == null) return null;
  let token = String(raw).trim();
  if (!token || token === 'undefined' || token === 'null') return null;
  // Unwrap one or more layers of JSON-quoted strings
  for (let i = 0; i < 3; i++) {
    if (
      (token.startsWith('"') && token.endsWith('"')) ||
      (token.startsWith("'") && token.endsWith("'"))
    ) {
      token = token.slice(1, -1).trim();
      continue;
    }
    if (token.startsWith('{') && token.includes('access_token')) {
      try {
        const parsed = JSON.parse(token);
        const inner = parsed?.access_token ?? parsed?.token;
        if (typeof inner === 'string' && inner) {
          token = inner.trim();
          continue;
        }
      } catch {
        break;
      }
    }
    break;
  }
  if (token.toLowerCase().startsWith('bearer ')) {
    token = token.slice(7).trim();
  }
  if (!token || token === 'debug-token') return null;
  if (!isLikelyJwt(token)) return null;
  return token;
}

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = sanitizeAuthToken(localStorage.getItem('authToken'));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If FormData, let axios set Content-Type automatically with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    if (API_DEBUG) {
      try {
        const url = (config.baseURL || '') + (config.url || '');
        // eslint-disable-next-line no-console
        console.log('[API][REQUEST]', config.method?.toUpperCase(), url, {
          params: config.params,
          hasBody: !!config.data,
          isFormData: config.data instanceof FormData,
        });
      } catch {}
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (API_DEBUG) {
      try {
        const url = (response.config.baseURL || '') + (response.config.url || '');
        // eslint-disable-next-line no-console
        console.log('[API][RESPONSE]', response.status, url);
      } catch {}
    }
    return response;
  },
  (error: AxiosError) => {
    if (API_DEBUG) {
      try {
        const cfg: any = error.config || {};
        const url = (cfg.baseURL || '') + (cfg.url || '');
        const status = (error.response && error.response.status) || 'NETWORK_ERROR';
        // eslint-disable-next-line no-console
        console.log('[API][ERROR]', status, url, {
          message: error.message,
        });
      } catch {}
    }
    const silentError = Boolean((error.config as any)?.silentError);
    if (error.response) {
      const { status, data } = error.response;
      const errorData = data as any;
      
      // Detect if the request is the login call itself — don't redirect on login failures
      const requestUrl = (error.config?.url || '').toString();
      const isLoginRequest = requestUrl.includes('/auth/login');
      const isOnLoginPage = /login/i.test(window.location.pathname);

      switch (status) {
        case 401:
          if (!isLoginRequest && !isOnLoginPage && shopUserKeepsSession()) {
            break;
          }
          if (!isLoginRequest && !isOnLoginPage) {
            if (!silentError) message.error('Session expired. Please login again.');
            localStorage.removeItem('authToken');
            window.location.href = getLoginRedirectUrl();
          }
          break;
        case 403: {
          // If 403 on superadmin API and not during a login request, redirect to dashboard
          const url = requestUrl;
          if (!isLoginRequest && !isOnLoginPage && url.includes('/superadmin')) {
            if (!silentError) message.error('Access forbidden. Insufficient permissions.');
            const currentUser = localStorage.getItem('currentUser');
            let toDashboard = '/dashboard';
            try {
              if (currentUser) {
                const parsed = JSON.parse(currentUser);
                const role = (parsed?.user?.roleName || '').toLowerCase();
                if (role.includes('admin') && !role.includes('super admin')) toDashboard = '/admin-portal/dashboard';
              }
            } catch (_) { /* ignore */ }
            window.location.href = toDashboard;
          }
          break;
        }
        case 404: {
          const url404 = requestUrl;
          // Optional / opt-in resources: callers handle missing endpoints locally
          // (e.g. My Profile GST/GL/payment when backend is older or feature off).
          const isOptionalAsset =
            url404.includes('/auth/me/company-logo') ||
            url404.includes('/me/company-logo') ||
            url404.includes('/superadmin/notifications') ||
            url404.includes('/company-admin/gst-settings') ||
            url404.includes('/company-admin/payment-settings') ||
            url404.includes('/gl/settings');
          if (!isOptionalAsset && !silentError) {
            message.error('Resource not found.');
          }
          break;
        }
        case 503: {
          if (!silentError) {
            const d = errorData?.detail || errorData?.message;
            message.error(
              typeof d === 'string'
                ? d
                : 'Service temporarily unavailable. The database or an upstream service may be unreachable.'
            );
          }
          break;
        }
        case 500:
          if (!silentError) message.error('Internal server error. Please try again later.');
          break;
        case 422:
          // Validation error - show detailed error message
          if (!silentError) {
            const validationErrors = errorData?.detail || errorData?.message;
            if (Array.isArray(validationErrors)) {
              // FastAPI validation errors format
              const errorMessages = validationErrors.map((err: any) => {
                const field = err.loc?.join('.') || 'field';
                const msg = err.msg || 'Invalid value';
                return `${field}: ${msg}`;
              }).join(', ');
              message.error(`Validation error: ${errorMessages}`);
            } else {
              message.error(validationErrors || 'Validation error. Please check your input.');
            }
          }
          break;
        default:
          if (!silentError) {
            message.error(errorData?.detail || errorData?.message || 'An unexpected error occurred.');
          }
      }
    } else if (error.request) {
      // Network error - request was made but no response received
      console.error('[API] Network error - no response received:', {
        message: error.message,
        code: error.code,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
      
      // Log network errors for debugging
      if (API_DEBUG) {
        console.warn('[API] Network error:', error.message);
        console.warn('[API] Error details:', {
          code: error.code,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          fullURL: (error.config?.baseURL || '') + (error.config?.url || '')
        });
      }
      
      // Callers can set silentError for enrich-only fetches (e.g. transaction detail).
      if (!silentError) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          if (!API_DEBUG) {
            message.error('Request timeout. The server is taking too long to respond. Please try again.');
          }
        } else if (!API_DEBUG) {
          message.error('Network error. Please check your connection and ensure the backend is running.');
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Generic API methods
export const apiService = {
  // GET request
  async get<T>(url: string, configOrParams?: any): Promise<T> {
    // Back-compat: many callers pass query params directly (e.g. apiService.get('/x', { active_only: false }))
    // Axios expects params to be nested under { params: ... }.
    const normalizeGetConfig = (value: any) => {
      if (!value) return value;
      if (typeof value !== 'object') return value;

      // If it already looks like an axios config, keep as-is.
      const axiosConfigKeys = new Set([
        'params',
        'headers',
        'timeout',
        'responseType',
        'signal',
        'auth',
        'baseURL',
        'method',
        'data',
        'withCredentials',
        'onUploadProgress',
        'onDownloadProgress',
        'validateStatus',
        'silentError',
      ]);

      for (const k of Object.keys(value)) {
        if (axiosConfigKeys.has(k)) return value;
      }

      // Otherwise treat it as a plain query params object.
      return { params: value };
    };

    const response = await apiClient.get<T>(url, normalizeGetConfig(configOrParams));
    return response.data as T;
  },

  // POST request
  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await apiClient.post<T>(url, data, config);
    return response.data as T;
  },

  // PUT request
  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await apiClient.put<T>(url, data, config);
    return response.data as T;
  },

  // DELETE request
  async delete<T>(url: string): Promise<T> {
    const response = await apiClient.delete<T>(url);
    return response.data as T;
  },

  // Upload file
  async upload<T>(url: string, formData: FormData): Promise<T> {
    const response = await apiClient.post<T>(url, formData, {
      timeout: 120000,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data as T;
  },

  // Download file
  async download(url: string): Promise<Blob> {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default apiClient;